import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type ListPipelineShapesResult } from '@whinerp/client';
import { Button, ErrorState, Num, Panel, SectionLabel, Skeleton } from '@whinerp/ui';
import { formatCount } from '@whinerp/contracts/format';
import { useFormat, useT } from '../../app/i18n.js';
import { StagePlaybookEditor } from './stage-playbook-editor.js';

/**
 * Shaping a pipeline (F-042).
 *
 * `pipeline_stage` has carried name, order, probability and outcome per
 * pipeline since the first sales migration, and nothing could set any of them.
 * A workspace got whatever provisioning or its industry pack gave it and lived
 * with the words.
 *
 * Every stage shows how many deals are sitting in it, beside the controls that
 * would change or remove it. A person about to rename Negotiation should be
 * able to see that eleven deals are in it without going to look, and the
 * refusals that follow name the same number.
 */

type Shapes = ListPipelineShapesResult;
type Pipeline = Shapes['pipelines'][number];
type Stage = Pipeline['stages'][number];

const OUTCOMES = ['open', 'won', 'lost'] as const;

export function PipelinesPanel(): JSX.Element {
  const t = useT();
  const format = useFormat();
  const queryClient = useQueryClient();
  const [newPipeline, setNewPipeline] = useState('');
  const [newStage, setNewStage] = useState<Record<string, string>>({});
  /** Which stage has its playbook open. One at a time: these are long. */
  const [openPlaybook, setOpenPlaybook] = useState<string | null>(null);

  const shapes = useQuery<Shapes, ApiError>({
    queryKey: ['pipeline-shapes'],
    queryFn: () => api.listPipelineShapes({}),
  });

  const refresh = (): Promise<unknown> =>
    queryClient.invalidateQueries({ queryKey: ['pipeline-shapes'] });

  const addPipeline = useMutation<unknown, ApiError>({
    mutationFn: () => api.savePipeline({ body: { label: newPipeline.trim() } }),
    onSuccess: async () => {
      setNewPipeline('');
      await refresh();
    },
  });

  const makeDefault = useMutation<unknown, ApiError, Pipeline>({
    mutationFn: (pipeline) =>
      api.savePipeline({ body: { pipelineId: pipeline.pipelineId, label: pipeline.label, isDefault: true } }),
    onSuccess: () => refresh(),
  });

  const removePipeline = useMutation<unknown, ApiError, string>({
    mutationFn: (pipelineId) => api.deletePipeline({ path: { pipelineId } }),
    onSuccess: () => refresh(),
  });

  const saveStage = useMutation<
    unknown,
    ApiError,
    { pipelineId: string; stageId?: string; label: string; probability: number; outcome: 'open' | 'won' | 'lost' }
  >({
    mutationFn: ({ pipelineId, ...body }) =>
      api.saveStage({ path: { pipelineId }, body }),
    onSuccess: async (_result, variables) => {
      setNewStage((current) => ({ ...current, [variables.pipelineId]: '' }));
      await refresh();
    },
  });

  const removeStage = useMutation<unknown, ApiError, string>({
    mutationFn: (stageId) => api.deleteStage({ path: { stageId } }),
    onSuccess: () => refresh(),
  });

  /*
   * The ramp, beside the playbooks it governs.
   *
   * The number here is the number the deal record opens the panel against -
   * the server compares it, and returns it with the playbook so the two cannot
   * drift into saying different things.
   */
  const ramp = useQuery<{ days: number }, ApiError>({
    queryKey: ['playbook-ramp'],
    queryFn: () => api.getPlaybookRamp({}),
  });

  const saveRamp = useMutation<{ days: number }, ApiError, number>({
    mutationFn: (days) => api.setPlaybookRamp({ body: { days } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['playbook-ramp'] }),
  });

  const reorder = useMutation<unknown, ApiError, { pipelineId: string; stageIds: string[] }>({
    mutationFn: ({ pipelineId, stageIds }) =>
      api.reorderStages({ path: { pipelineId }, body: { stageIds } }),
    onSuccess: () => refresh(),
  });

  if (shapes.isPending) return <Skeleton className="h-block-lg w-full" />;
  if (shapes.isError) {
    return (
      <ErrorState title={t('state.error')} body={shapes.error.message} onRetry={() => void shapes.refetch()} />
    );
  }

  const failure =
    addPipeline.error ??
    makeDefault.error ??
    removePipeline.error ??
    saveStage.error ??
    removeStage.error ??
    saveRamp.error ??
    reorder.error;

  /** Moves one stage, by sending the whole order. */
  const move = (pipeline: Pipeline, stage: Stage, by: number): void => {
    const ids = pipeline.stages.map((entry) => entry.stageId);
    const from = ids.indexOf(stage.stageId);
    const to = from + by;
    if (to < 0 || to >= ids.length) return;
    const next = [...ids];
    next.splice(from, 1);
    next.splice(to, 0, stage.stageId);
    reorder.mutate({ pipelineId: pipeline.pipelineId, stageIds: next });
  };

  return (
    <Panel data-region="pipelines" id="pipelines">
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <SectionLabel>{t('pipelines.title')}</SectionLabel>
          <p className="text-meta text-ink-meta">{t('pipelines.body')}</p>
        </div>

        {failure ? (
          <p data-pipelines-refusal className="rounded-control bg-danger-tint px-3 py-2 text-dense text-danger-text">
            {failure.message}
          </p>
        ) : null}

        {ramp.isSuccess ? (
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex flex-wrap items-center gap-2 text-dense text-ink-600">
              {t('playbook.ramp')}
              <input
                data-playbook-ramp
                inputMode="numeric"
                className="w-col-count rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
                defaultValue={String(ramp.data.days)}
                onBlur={(event) => {
                  const days = Number(event.target.value);
                  if (days === ramp.data.days) return;
                  saveRamp.mutate(days);
                }}
              />
              {t('playbook.ramp.days')}
            </label>
            {/* Zero is a setting, not an empty field, so it says what it does. */}
            {ramp.data.days === 0 ? (
              <span data-playbook-ramp-off className="text-meta text-ink-meta">
                {t('playbook.ramp.off')}
              </span>
            ) : null}
          </div>
        ) : null}

        {shapes.data.pipelines.map((pipeline) => (
          <div
            key={pipeline.pipelineId}
            data-pipeline={pipeline.pipelineId}
            className="flex flex-col gap-2 border-t border-t-rule pt-3 first:border-t-0 first:pt-0"
          >
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="min-w-0 flex-1 truncate text-body font-medium text-ink">
                {pipeline.label}
              </span>
              {pipeline.isDefault ? (
                <span data-pipeline-default={pipeline.pipelineId} className="text-micro uppercase tracking-label text-bronze-text">
                  {t('pipelines.default')}
                </span>
              ) : (
                <Button
                  intent="ghost"
                  size="small"
                  data-pipeline-make-default={pipeline.pipelineId}
                  onClick={() => makeDefault.mutate(pipeline)}
                >
                  {t('pipelines.makeDefault')}
                </Button>
              )}
              <Button
                intent="ghost"
                size="small"
                data-pipeline-remove={pipeline.pipelineId}
                onClick={() => removePipeline.mutate(pipeline.pipelineId)}
              >
                {t('pipelines.remove')}
              </Button>
            </div>

            <ul className="flex flex-col">
              {pipeline.stages.map((stage) => (
                <li
                  key={stage.stageId}
                  data-stage={stage.stageId}
                  className="flex flex-wrap items-center gap-2 border-t border-t-rule py-1 first:border-t-0"
                >
                  <input
                    data-stage-label={stage.stageId}
                    aria-label={`${pipeline.label}, ${stage.label}, ${t('pipelines.stage.name')}`}
                    className="min-w-0 flex-1 rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
                    defaultValue={stage.label}
                    onBlur={(event) => {
                      const label = event.target.value.trim();
                      if (label === '' || label === stage.label) return;
                      saveStage.mutate({
                        pipelineId: pipeline.pipelineId,
                        stageId: stage.stageId,
                        label,
                        probability: Math.round(Number(stage.probability)),
                        outcome: stage.outcome,
                      });
                    }}
                  />
                  <input
                    data-stage-probability={stage.stageId}
                    aria-label={`${stage.label}, ${t('pipelines.stage.probability')}`}
                    inputMode="numeric"
                    className="w-col-count rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
                    defaultValue={String(Math.round(Number(stage.probability)))}
                    onBlur={(event) => {
                      const probability = Number(event.target.value);
                      if (probability === Math.round(Number(stage.probability))) return;
                      saveStage.mutate({
                        pipelineId: pipeline.pipelineId,
                        stageId: stage.stageId,
                        label: stage.label,
                        probability,
                        outcome: stage.outcome,
                      });
                    }}
                  />
                  <select
                    data-stage-outcome={stage.stageId}
                    aria-label={`${stage.label}, ${t('pipelines.stage.outcome')}`}
                    className="rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
                    value={stage.outcome}
                    onChange={(event) =>
                      saveStage.mutate({
                        pipelineId: pipeline.pipelineId,
                        stageId: stage.stageId,
                        label: stage.label,
                        probability: Math.round(Number(stage.probability)),
                        outcome: event.target.value as 'open' | 'won' | 'lost',
                      })
                    }
                  >
                    {OUTCOMES.map((outcome) => (
                      <option key={outcome} value={outcome}>
                        {t(`pipelines.outcome.${outcome}` as 'pipelines.outcome.open')}
                      </option>
                    ))}
                  </select>
                  {/* What a change would touch, before it is made. */}
                  <span data-stage-deals={stage.stageId} className="shrink-0 text-meta text-ink-meta">
                    {stage.deals === 0 ? (
                      t('pipelines.stage.empty')
                    ) : (
                      <Num>{t('pipelines.stage.deals', { values: { count: formatCount(stage.deals, format) } })}</Num>
                    )}
                  </span>
                  <Button
                    intent="ghost"
                    size="small"
                    data-stage-up={stage.stageId}
                    onClick={() => move(pipeline, stage, -1)}
                  >
                    {t('pipelines.stage.up')}
                  </Button>
                  <Button
                    intent="ghost"
                    size="small"
                    data-stage-down={stage.stageId}
                    onClick={() => move(pipeline, stage, 1)}
                  >
                    {t('pipelines.stage.down')}
                  </Button>
                  <Button
                    intent="ghost"
                    size="small"
                    data-stage-playbook={stage.stageId}
                    onClick={() =>
                      setOpenPlaybook((current) => (current === stage.stageId ? null : stage.stageId))
                    }
                  >
                    {t('playbook.edit')}
                  </Button>
                  <Button
                    intent="ghost"
                    size="small"
                    data-stage-remove={stage.stageId}
                    onClick={() => removeStage.mutate(stage.stageId)}
                  >
                    {t('pipelines.stage.remove')}
                  </Button>
                  {openPlaybook === stage.stageId ? (
                    <div className="w-full">
                      <StagePlaybookEditor stageId={stage.stageId} stageLabel={stage.label} />
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>

            <div className="flex flex-wrap items-end gap-2">
              <input
                data-stage-new={pipeline.pipelineId}
                aria-label={`${pipeline.label}, ${t('pipelines.stage.new')}`}
                placeholder={t('pipelines.stage.name')}
                className="min-w-0 flex-1 rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
                value={newStage[pipeline.pipelineId] ?? ''}
                onChange={(event) =>
                  setNewStage((current) => ({ ...current, [pipeline.pipelineId]: event.target.value }))
                }
              />
              <Button
                intent="secondary"
                size="small"
                data-stage-add={pipeline.pipelineId}
                disabled={(newStage[pipeline.pipelineId] ?? '').trim() === ''}
                onClick={() =>
                  saveStage.mutate({
                    pipelineId: pipeline.pipelineId,
                    label: (newStage[pipeline.pipelineId] ?? '').trim(),
                    probability: 50,
                    outcome: 'open',
                  })
                }
              >
                {t('pipelines.stage.new')}
              </Button>
            </div>
          </div>
        ))}

        <div className="flex flex-wrap items-end gap-2 border-t border-t-rule pt-3">
          <input
            data-pipeline-new
            aria-label={t('pipelines.new')}
            placeholder={t('pipelines.name')}
            className="min-w-0 flex-1 rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink"
            value={newPipeline}
            onChange={(event) => setNewPipeline(event.target.value)}
          />
          <Button
            intent="secondary"
            size="small"
            data-pipeline-add
            disabled={newPipeline.trim() === ''}
            onClick={() => addPipeline.mutate()}
          >
            {t('pipelines.new')}
          </Button>
        </div>
      </div>
    </Panel>
  );
}
